import { useState, useEffect, useRef } from "react";
import Map, { Marker, Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { trackEvent } from "../../utils/analytics";

const MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const HomePin = () => (
  <div className="w-9 h-9 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white ring-offset-1">
    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
  </div>
);

const DestPin = ({ index, name, showIndex }) => (
  <div className="relative group cursor-default">
    <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center shadow-md ring-2 ring-white transition-transform duration-200 hover:scale-110">
      {showIndex ? (
        <span className="text-[11px] font-bold text-white">{index}</span>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 bg-gray-800 text-white shadow pointer-events-none transition-opacity duration-150 z-10">
      {name}
    </div>
  </div>
);

const Step1Map = ({ fromLocation, destinations = [] }) => {
  const mapRef = useRef(null);
  const mapTrackedRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  // WebGL context creation can fail (GPU disabled/sandboxed browser, old
  // hardware, etc.) — maplibre never fires onLoad in that case, so without
  // this the component would show a blank map area forever.
  const [mapError, setMapError] = useState(false);

  // Build markers list inline — cheap, no async needed
  const markers = [];

  const locationCounts = {};
  const extractCoords = (loc) => {
    if (!loc) return null;
    let lat = loc.lat ?? loc.latitude ?? loc.geometry?.location?.lat;
    let lon = loc.lon ?? loc.lng ?? loc.longitude ?? loc.geometry?.location?.lng;
    if (typeof lat === 'function') lat = lat();
    if (typeof lon === 'function') lon = lon();
    if (lat != null && lon != null) return { lat: parseFloat(lat), lng: parseFloat(lon) };
    return null;
  };

  const getOverlapIndex = (lat, lng) => {
    const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    const count = locationCounts[key] || 0;
    locationCounts[key] = count + 1;
    return count;
  };

  const startCoords = extractCoords(fromLocation);
  if (startCoords) {
    markers.push({
      name: fromLocation.name || fromLocation.city || "Start",
      lat: startCoords.lat,
      lng: startCoords.lng,
      isStart: true,
      overlapIndex: getOverlapIndex(startCoords.lat, startCoords.lng),
    });
  }

  destinations.forEach((d, i) => {
    const coords = extractCoords(d);
    if (coords) {
      markers.push({
        name: d.name,
        lat: coords.lat,
        lng: coords.lng,
        index: i + 1,
        overlapIndex: getOverlapIndex(coords.lat, coords.lng)
      });
    }
  });

  const LINE_COLORS = [
    "#14b8a6", // teal-500
    "#3b82f6", // blue-500
    "#f59e0b", // amber-500
    "#ec4899", // pink-500
    "#8b5cf6", // violet-500
    "#ef4444", // red-500
  ];

  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const distanceMarkers = [];
  const routeFeatures = [];

  for (let i = 1; i < markers.length; i++) {
    const prev = markers[i - 1];
    const curr = markers[i];
    const dist = haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);

    distanceMarkers.push({
      lng: (prev.lng + curr.lng) / 2,
      lat: (prev.lat + curr.lat) / 2,
      text: `${dist} km`,
      color: LINE_COLORS[(i - 1) % LINE_COLORS.length],
    });

    routeFeatures.push({
      type: "Feature",
      properties: {
        color: LINE_COLORS[(i - 1) % LINE_COLORS.length],
      },
      geometry: {
        type: "LineString",
        coordinates: [
          [prev.lng, prev.lat],
          [curr.lng, curr.lat],
        ],
      },
    });
  }

  const destCount = markers.filter((m) => !m.isStart).length;

  // Stable string key so the fit effect only fires when coordinates actually change
  const boundsKey = markers.map((m) => `${m.lat},${m.lng}`).join("|");

  const markersRef = useRef(markers);
  useEffect(() => {
    markersRef.current = markers;
  });

  useEffect(() => {
    let isTouring = false;
    let tourTimeout = null;

    const handleTour = async () => {
      if (!mapRef.current || markersRef.current.length < 2 || isTouring) return;
      isTouring = true;

      const tourMarkers = markersRef.current;

      // Briefly zoom out to see the whole route before starting
      const lngs = tourMarkers.map((m) => m.lng);
      const lats = tourMarkers.map((m) => m.lat);
      const padDeg = tourMarkers.length <= 2 ? 1.5 : 0.8;
      mapRef.current.fitBounds(
        [
          [Math.min(...lngs) - padDeg, Math.min(...lats) - padDeg],
          [Math.max(...lngs) + padDeg, Math.max(...lats) + padDeg],
        ],
        { duration: 1500, padding: 48, pitch: 0, bearing: 0 },
      );

      await new Promise(r => { tourTimeout = setTimeout(r, 1000); });

      for (let i = 0; i < tourMarkers.length; i++) {
        if (!isTouring) break;
        const marker = tourMarkers[i];
        mapRef.current.flyTo({
          center: [marker.lng, marker.lat],
          zoom: 8,
          pitch: 50,
          bearing: i * 20,
          duration: 2000,
          essential: true
        });

        await new Promise(r => { tourTimeout = setTimeout(r, 2500); });
      }

      if (isTouring && mapRef.current) {
        mapRef.current.fitBounds(
          [
            [Math.min(...lngs) - padDeg, Math.min(...lats) - padDeg],
            [Math.max(...lngs) + padDeg, Math.max(...lats) + padDeg],
          ],
          { duration: 2000, padding: 48, pitch: 0, bearing: 0 },
        );
      }
      isTouring = false;
    };

    window.addEventListener("start-map-tour", handleTour);
    return () => {
      window.removeEventListener("start-map-tour", handleTour);
      isTouring = false;
      if (tourTimeout) clearTimeout(tourTimeout);
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || markers.length === 0) return;

    if (markers.length === 1) {
      mapRef.current.flyTo({
        center: [markers[0].lng, markers[0].lat],
        zoom: 9,
        duration: 800,
      });
    } else {
      const lngs = markers.map((m) => m.lng);
      const lats = markers.map((m) => m.lat);
      const padDeg = markers.length <= 2 ? 1.5 : 0.8;
      mapRef.current.fitBounds(
        [
          [Math.min(...lngs) - padDeg, Math.min(...lats) - padDeg],
          [Math.max(...lngs) + padDeg, Math.max(...lats) + padDeg],
        ],
        { duration: 900, padding: 48 },
      );
    }
    // boundsKey encodes all coordinates; markers is fresh from same render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, boundsKey]);

  if (mapError) {
    // maplibre needs WebGL; some browsers/sandboxed environments refuse to
    // grant a GL context at all (see onError below). OSM's embeddable iframe
    // is plain HTML/tiles — no WebGL required — so it still shows a real map
    // in exactly the situations where the WebGL map cannot render anything.
    if (markers.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-xl">
          <div className="text-center px-6">
            <p className="text-gray-600 text-sm font-medium">Map couldn't load</p>
            <p className="text-gray-400 text-xs mt-1">
              Your browser blocked the map's graphics — try a different browser or enable hardware acceleration.
            </p>
          </div>
        </div>
      );
    }

    const lats = markers.map((m) => m.lat);
    const lngs = markers.map((m) => m.lng);
    const padDeg = 0.35;
    const minLat = Math.min(...lats) - padDeg;
    const maxLat = Math.max(...lats) + padDeg;
    const minLng = Math.min(...lngs) - padDeg;
    const maxLng = Math.max(...lngs) + padDeg;
    const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
    const marker = `${markers[0].lat},${markers[0].lng}`;
    const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`;

    return (
      <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100">
        <iframe
          title="Trip route map"
          src={osmSrc}
          className="w-full h-full border-0"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <Map
        ref={mapRef}
        initialViewState={{ longitude: 78.9629, latitude: 20.5937, zoom: 3 }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLE}
        onLoad={() => {
          setMapLoaded(true);

          if (!mapTrackedRef.current) {
            trackEvent("map_loaded");
            mapTrackedRef.current = true;
          }
        }}
        onError={(e) => {
          console.warn('[Step1Map] failed to load:', e?.error?.message || e);
          setMapError(true);
        }}
        attributionControl={false}
        dragRotate={false}
        touchPitch={false}
      >
        {routeFeatures.length > 0 && (
          <Source
            id="step1-route"
            type="geojson"
            data={{
              type: "FeatureCollection",
              features: routeFeatures,
            }}
          >
            <Layer
              id="step1-route-line"
              type="line"
              paint={{
                "line-color": ["get", "color"],
                "line-width": 3,
                "line-opacity": 0.85,
              }}
            />
          </Source>
        )}

        {distanceMarkers.map((dm, idx) => (
          <Marker
            key={`dist-step1-${idx}`}
            longitude={dm.lng}
            latitude={dm.lat}
            anchor="center"
          >
            <div
              className="bg-white/95 backdrop-blur px-1.5 py-0.5 rounded shadow text-[10px] font-medium whitespace-nowrap border"
              style={{ color: dm.color, borderColor: dm.color }}
            >
              {dm.text}
            </div>
          </Marker>
        ))}

        {markers.map((marker, i) => {
          const offsetAmount = 14; // pixels
          const xOffset = marker.overlapIndex * offsetAmount;
          const yOffset = marker.overlapIndex * -offsetAmount;
          return (
            <Marker
              key={`step1-${i}-${marker.lat}-${marker.lng}-${marker.overlapIndex}`}
              longitude={marker.lng}
              latitude={marker.lat}
              anchor="center"
              offset={[xOffset, yOffset]}
              style={{ zIndex: marker.overlapIndex }}
            >
              {marker.isStart ? (
                <HomePin />
              ) : (
                <DestPin
                  index={marker.index}
                  name={marker.name}
                  showIndex={destCount > 1}
                />
              )}
            </Marker>
          );
        })}
      </Map>

      {/* Zoom controls */}
      <div className="absolute top-3 left-3 flex flex-col gap-1">
        <button
          onClick={() => mapRef.current?.zoomIn({ duration: 300 })}
          className="w-7 h-7 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
          title="Zoom in"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v12m6-6H6"
            />
          </svg>
        </button>
        <button
          onClick={() => mapRef.current?.zoomOut({ duration: 300 })}
          className="w-7 h-7 bg-white rounded-lg shadow flex items-center justify-center text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
          title="Zoom out"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 12H6"
            />
          </svg>
        </button>
      </div>

      {/* Fit all button */}
      {markers.length > 0 && (
        <button
          onClick={() => {
            if (!mapRef.current || markers.length === 0) return;
            if (markers.length === 1) {
              mapRef.current.flyTo({
                center: [markers[0].lng, markers[0].lat],
                zoom: 9,
                duration: 600,
              });
            } else {
              const lngs = markers.map((m) => m.lng);
              const lats = markers.map((m) => m.lat);
              const padDeg = markers.length <= 2 ? 1.5 : 0.8;
              mapRef.current.fitBounds(
                [
                  [Math.min(...lngs) - padDeg, Math.min(...lats) - padDeg],
                  [Math.max(...lngs) + padDeg, Math.max(...lats) + padDeg],
                ],
                { duration: 700, padding: 48 },
              );
            }
          }}
          className="absolute bottom-3 right-3 px-2.5 py-1 bg-white rounded-lg shadow text-[11px] font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          title="Fit all"
        >
          ⛶ Fit All
        </button>
      )}

      {/* Legend */}
      {markers.length > 0 && (
        <div className="absolute top-3 right-3 flex items-center gap-2.5 px-2.5 py-1.5 bg-white/90 backdrop-blur rounded-lg shadow text-[10px]">
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-gray-600">From</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-teal-500 flex-shrink-0" />
            <span className="text-gray-600">Destination</span>
          </div>
        </div>
      )}

      {/* Map Tour Button */}
      {markers.length >= 2 && (
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent("start-map-tour"));
            trackEvent("map_tour_started");
          }}
          className="absolute bottom-3 left-3 px-3.5 py-2 bg-teal-50/95 hover:bg-white text-teal-700 hover:text-teal-800 rounded-full shadow-md hover:shadow-lg flex items-center gap-1.5 transition-all duration-300 transform hover:-translate-y-1 cursor-pointer text-xs font-bold border border-teal-200/60 backdrop-blur group"
          title="Start Map Tour"
        >
          <div className="relative flex items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-30 group-hover:animate-ping"></span>
            <svg className="w-4 h-4 text-teal-600 group-hover:text-teal-700 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          Map Tour
        </button>
      )}

      {/* Empty state hint */}
      {markers.length === 0 && mapLoaded && (
        <div className="absolute inset-0 flex items-end justify-center pb-5 pointer-events-none">
          <div className="bg-white/85 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
            <p className="text-xs text-gray-500">
              Your route will appear here as you plan
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1Map;
